import * as React from 'react';

import { enumLoadStatus, LoadStatus } from '@rematter/frontend-lib-api-client';
import { Button, Menu } from '@rematter/ui-web';
import { Eye, MoreVertical, PackageCheck, Ship, Trash2 } from 'lucide-react';

import { useTranslations } from '../../i18n/useTranslations';
import { BrokeredLoadRecord } from '../../types/brokeredLoad';

import { brokerageRoutePaths } from '~/modules/brokerage/routes';
import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

type BrokeredLoadActionMenuProps = {
  load: BrokeredLoadRecord;
  onRefetch?: () => void;
  onVoid?: (loadId: string) => void;
  onMarkAsShipped: (loadId: string) => void;
  onMarkAsReceived: (loadId: string) => void;
};

const canMarkAsShipped = (status: LoadStatus) =>
  status === enumLoadStatus.Pending;

const canMarkAsReceived = (status: LoadStatus) =>
  status === enumLoadStatus.Shipped;

const canVoid = (status: LoadStatus) =>
  status !== enumLoadStatus.Voided && status !== enumLoadStatus.Reconciled;

export const BrokeredLoadActionMenu: React.FC<BrokeredLoadActionMenuProps> = ({
  load,
  onVoid,
  onMarkAsShipped,
  onMarkAsReceived,
}) => {
  const t = useTranslations();
  const [, navigate] = useModuleLocation();

  const handleView = React.useCallback(() => {
    navigate(brokerageRoutePaths.loadDetail, { params: { id: load.id } });
  }, [navigate, load.id]);

  const handleMarkAsShipped = React.useCallback(() => {
    onMarkAsShipped(load.id);
  }, [onMarkAsShipped, load.id]);

  const handleMarkAsReceived = React.useCallback(() => {
    onMarkAsReceived(load.id);
  }, [onMarkAsReceived, load.id]);

  const handleVoid = React.useCallback(() => {
    onVoid?.(load.id);
  }, [onVoid, load.id]);

  const menuItems = React.useMemo(() => {
    const items: Array<{
      label: React.ReactNode;
      onAction: () => void;
      variant?: 'destructive';
    }> = [
      {
        label: (
          <div className="flex items-center gap-2">
            <Eye className="size-4" />
            {t.brokerage.loads.actions.view}
          </div>
        ),
        onAction: handleView,
      },
    ];

    if (canMarkAsShipped(load.status)) {
      items.push({
        label: (
          <div className="flex items-center gap-2">
            <Ship className="size-4" />
            {t.brokerage.loads.actions.markAsShipped}
          </div>
        ),
        onAction: handleMarkAsShipped,
      });
    }

    if (canMarkAsReceived(load.status)) {
      items.push({
        label: (
          <div className="flex items-center gap-2">
            <PackageCheck className="size-4" />
            {t.brokerage.loads.actions.markAsReceived}
          </div>
        ),
        onAction: handleMarkAsReceived,
      });
    }

    if (canVoid(load.status)) {
      items.push({
        label: (
          <div className="flex items-center gap-2 text-failure">
            <Trash2 className="size-4" />
            {t.brokerage.loads.actions.void}
          </div>
        ),
        onAction: handleVoid,
        variant: 'destructive',
      });
    }

    return items;
  }, [
    t,
    load.status,
    handleView,
    handleMarkAsShipped,
    handleMarkAsReceived,
    handleVoid,
  ]);

  return (
    <div className="@BrokeredLoadActionMenu flex justify-center">
      <Menu
        trigger={
          <Button size="icon" icon={<MoreVertical />} variant="outline" />
        }
        items={menuItems}
      />
    </div>
  );
};
